import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TextInput } from '../../../../../components/input/TextInput'
import FormSection from '../../../../../components/formSection/FormSection'
import { CheckboxInput } from '../../../../../components/input/checkboxInput/CheckboxInput'
import { PasswordCreateInput } from '../../../../../components/input/passwordCreateInput/PasswordCreateInput'

import { InputType } from '../../../../../constants/inputTypes'
import type { FormField } from '../../../../../types/formData'

export type GuestUserDetails = {
  firstName: FormField<string>
  lastName: FormField<string>
  email: FormField<string>
}

type GuestUserDetailsStepProps = {
  details: GuestUserDetails
  includeAccount: boolean
  password: FormField<string>
  termsConsent: FormField<boolean>
  onDetailsChange: (name: string, value: string, isValid: boolean) => void
  onAccountCreationChange: (name: string, value: boolean) => void
  onPasswordChange: (value: string, isValid: boolean) => void
  onTermsChange: (value: boolean, isValid: boolean) => void
}

const GuestUserDetailsStep = ({
  details,
  includeAccount,
  password,
  termsConsent,
  onDetailsChange,
  onAccountCreationChange,
  onPasswordChange,
  onTermsChange,
}: GuestUserDetailsStepProps) => {
  const { t } = useTranslation(['bookings', 'register', 'auth'])

  return (
    <FormSection
      title={t('booking.guest-checkout.details.title')}
      subtitle={t('booking.guest-checkout.details.subtitle')}
    >
      <div className="row g-3 mt-1">
        <div className="col-12 col-md-6">
          <TextInput
            name="firstName"
            label={t('booking.guest-checkout.details.first-name')}
            value={details.firstName.value}
            required
            errorMessage={t('booking.guest-checkout.validation.required')}
            onChange={onDetailsChange}
          />
        </div>

        <div className="col-12 col-md-6">
          <TextInput
            name="lastName"
            label={t('booking.guest-checkout.details.last-name')}
            value={details.lastName.value}
            required
            errorMessage={t('booking.guest-checkout.validation.required')}
            onChange={onDetailsChange}
          />
        </div>

        <div className="col-12">
          <TextInput
            name="email"
            type={InputType.Email}
            label={t('booking.guest-checkout.details.email')}
            value={details.email.value}
            required
            errorMessage={t('booking.guest-checkout.validation.email')}
            onChange={onDetailsChange}
          />
        </div>

        <div className="col-12 guest-checkout-create-account">
          <CheckboxInput
            name="createAccount"
            checked={includeAccount}
            onChange={onAccountCreationChange}
            label={t('booking.guest-checkout.account.create')}
          />
        </div>

        {includeAccount && (
          <div className="col-12 guest-checkout-account">
            <PasswordCreateInput
              name="password"
              label={t('auth:password')}
              placeHolder={t('auth:password')}
              confirmLabel={t('auth:confirm-password')}
              confirmPlaceHolder={t('auth:confirm-password')}
              value={password.value}
              errorMessage={t('register:password-error')}
              onChange={(_, value, isValid) =>
                onPasswordChange(value, isValid)
              }
            />

            <div className="guest-checkout-terms">
              <CheckboxInput
                name="termsConsent"
                required
                checked={termsConsent.value}
                onChange={(_, value, isValid) =>
                  onTermsChange(value, isValid)
                }
                label={
                  <Trans
                    i18nKey="register:terms"
                    components={{
                      terms: <Link to="/terms" />,
                      privacy: <Link to="/terms#c10" />,
                    }}
                  />
                }
              />
            </div>
          </div>
        )}
      </div>
    </FormSection>
  )
}

export default GuestUserDetailsStep
